import { useCallback, useMemo } from 'react';

import type { AgentCreatorType } from '@atlaskit/rovo-agent-components/common/types';
import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import {
	type AgentKnowledgeConfiguration,
	type AgentKnowledgeConfigurationResponse,
} from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { useQuery } from '@atlassian/conversation-assistant-service/common/utils/use-query';
import {
	type AssistanceServiceInjectedParams,
	type AssistanceServiceInputParam,
	createHookWithAssistanceService,
} from '@atlassian/conversation-assistant-service/services/assistance-service/main';

import { ROVO_AGENTS_FE_TEAM_NAME } from '../../common/constants/private.ts';

/** Do not import for non Atlassian studio */
export const useKnowledgeConnectors: (
	props: Omit<
		{
			enabled?: boolean;
			agentCreatorType?: AgentCreatorType;
		} & AssistanceServiceInjectedParams,
		'assistanceServiceInjectedParams'
	> &
		AssistanceServiceInputParam,
) => {
	enabledKnowledgeSources: AgentKnowledgeConfiguration[];
	isConnectorEnabled: (type: AgentKnowledgeConfiguration['source']) => boolean;
	isFetching: boolean;
	refetch: () => Promise<AgentKnowledgeConfigurationResponse | null>;
} = createHookWithAssistanceService(
	({
		assistanceServiceInjectedParams: { assistanceService, experienceTracker },
		enabled = true,
		agentCreatorType,
	}: {
		enabled?: boolean;
		agentCreatorType?: AgentCreatorType;
	} & AssistanceServiceInjectedParams) => {
		const { data, isFetching, refetch } = useQuery({
			queryKey: ['getKnowledgeConnectors', agentCreatorType ?? ''],
			queryFn: async () => {
				experienceTracker.start({
					id: ExperienceName.FETCH_CONNECTED_KNOWLEDGE_SOURCES,
					name: ExperienceName.FETCH_CONNECTED_KNOWLEDGE_SOURCES,
				});

				return assistanceService.getAgentKnowledgeConfiguration(agentCreatorType);
			},
			onSuccess: () => {
				experienceTracker.succeed({
					name: ExperienceName.FETCH_CONNECTED_KNOWLEDGE_SOURCES,
				});
			},
			onError: (error) => {
				experienceTracker.fail({
					name: ExperienceName.FETCH_CONNECTED_KNOWLEDGE_SOURCES,
					error,
					teamName: ROVO_AGENTS_FE_TEAM_NAME,
				});
			},
			enabled,
		});

		const isConnectorEnabled = useCallback(
			(type: AgentKnowledgeConfiguration['source']): boolean => {
				return data?.knowledgeSources?.some((value) => value.source === type) ?? false;
			},
			[data],
		);

		const enabledKnowledgeSources = useMemo((): AgentKnowledgeConfiguration[] => {
			return data?.knowledgeSources ?? [];
		}, [data]);

		return {
			enabledKnowledgeSources,
			isConnectorEnabled,
			isFetching,
			refetch,
		};
	},
);
